import { useRef, useState } from "react";
import { IRegister } from "../models/IRegister";
import { IRegisterResponse } from "../models/IRegisterResponse";
import { register, IApiError } from "../services/userService";
import { loginUser } from "../services/auth";
import { clearUserStorage, saveUserToken, getDeviceId } from "../utils/tokenUtils";
import OtpVerificationPopup from "./OtpVerificationPopup";
import EmailVerificationPopup from "./EmailVerificationPopup";
import BirthDatePicker from "../components/BirthDatePicker";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import { LEGAL_PATHS, MIN_USER_AGE } from "../legal/legalContent";

const maxBirthDate = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MIN_USER_AGE);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
})();

interface RegisterPopupProps {
  onLoginSuccess: () => void;
}

function RegisterPopup({ onLoginSuccess }: RegisterPopupProps) {
  const { t } = useLanguage();
  const closeRef = useRef<HTMLButtonElement>(null);
  const otpTriggerRef = useRef<HTMLButtonElement>(null);
  const emailTriggerRef = useRef<HTMLButtonElement>(null);
  const [emailVerifId, setEmailVerifId] = useState("");

  const [form, setForm] = useState<IRegister>({
    customerName: "",
    customerSurName: "",
    customerUserName: "",
    customerTelNo: "",
    customerEmail: "",
    customerBirthDate: "",
    customerIsEnabled: true,
    customerLogin: { customerPassword: "" },
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registerResponse, setRegisterResponse] = useState<IRegisterResponse | null>(null);
  const [otpSentAt, setOtpSentAt] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "customerPassword") {
      setForm((prev) => ({ ...prev, customerLogin: { customerPassword: value } }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (passwordError) setPasswordError("");
  };

  const openEmailModal = (verifId: string) => {
    setEmailVerifId(verifId);
    setTimeout(() => emailTriggerRef.current?.click(), 50);
  };

  const openOtpModal = () => {
    const registerEl = document.getElementById("registerModal");
    if (!registerEl) return;
    const handleHidden = () => {
      registerEl.removeEventListener("hidden.bs.modal", handleHidden);
      otpTriggerRef.current?.click();
    };
    registerEl.addEventListener("hidden.bs.modal", handleHidden);
    closeRef.current?.click(); // closeRef artık modalın içinde — düzgün çalışır
  };

  const handleRegister = async () => {
    if (form.customerLogin.customerPassword !== confirmPassword) {
      setPasswordError(t("register.passwordMismatch"));
      return;
    }
    setPasswordError("");
    if (!form.customerBirthDate) {
      setApiError(t("register.birthDateRequired"));
      return;
    }
    if (!acceptedTerms) {
      setApiError(t("register.consentRequired"));
      return;
    }
    setApiError("");
    setLoading(true);

    const payload: IRegister = {
      ...form,
      customerUserName: form.customerEmail,
      customerIsEnabled: true,
      customerBirthDate: new Date(form.customerBirthDate).toISOString(),
    };

    try {
      const res = await register(payload);
      setRegisterResponse(res.data);
      setOtpSentAt(Date.now());

      // Register sonrası login dene — başarısızsa OTP ile token alınır
      try {
        const loginRes = await loginUser(form.customerEmail, form.customerLogin.customerPassword, getDeviceId());
        clearUserStorage();
        saveUserToken({
          token: loginRes.data.token,
          expiration: loginRes.data.expiration,
          secretKey: loginRes.data.secretKey,
          userId: loginRes.data.userId,
          refreshToken: loginRes.data.refreshToken,
          refreshTokenExpiration: loginRes.data.refreshTokenExpiration,
        });
      } catch {
        // Kayıt sonrası login reddedildi — OTP doğrulaması token verecek
      }

      if (!res.data.verification.customerTelNoVerified) {
        openOtpModal();
      } else {
        onLoginSuccess();
      }
    } catch (err) {
      if (axios.isAxiosError(err) && Array.isArray(err.response?.data)) {
        const errors: IApiError[] = err.response!.data;
        setApiError(errors.map((e) => e.description).join(" "));
      } else {
        setApiError(t("register.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* otpTriggerRef modal DIŞINDA — yeni modal açmak için */}
      <button ref={otpTriggerRef} className="d-none" data-bs-toggle="modal" data-bs-target="#otpVerificationModal" />
      {/* emailTriggerRef — email doğrulama modalını açmak için */}
      <button ref={emailTriggerRef} className="d-none" data-bs-toggle="modal" data-bs-target="#emailVerificationModal" />

      <div
        className="modal fade"
        id="registerModal"
        tabIndex={-1}
        aria-labelledby="registerModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content rounded-4 shadow">
            <div className="modal-header border-0 pb-0">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={t("common.close")}
              ></button>
            </div>

            <div className="modal-body px-4 pb-4">
              {/* closeRef modal İÇİNDE — data-bs-dismiss="modal" çalışması için */}
              <button ref={closeRef} className="d-none" data-bs-dismiss="modal" />
              <div className="text-center mb-4">
                <i
                  className="bi bi-person-plus-fill"
                  style={{ fontSize: "3.5rem", color: "#c20e1a" }}
                ></i>
                <h5 className="mt-2 fw-bold" id="registerModalLabel">
                  {t("register.title")}
                </h5>
                <p className="text-muted small">{t("register.subtitle")}</p>
              </div>

              <div className="row g-3">
                <div className="col-12 col-sm-6">
                  <label className="form-label fw-semibold">{t("register.firstName")}</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-person"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      name="customerName"
                      placeholder={t("register.firstNamePlaceholder")}
                      value={form.customerName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-12 col-sm-6">
                  <label className="form-label fw-semibold">{t("register.lastName")}</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-person"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      name="customerSurName"
                      placeholder={t("register.lastNamePlaceholder")}
                      value={form.customerSurName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">{t("register.email")}</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-envelope"></i>
                    </span>
                    <input
                      type="email"
                      className="form-control"
                      name="customerEmail"
                      placeholder={t("login.emailPlaceholder")}
                      value={form.customerEmail}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">{t("register.phone")}</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-telephone"></i>
                    </span>
                    <input
                      type="tel"
                      className="form-control"
                      name="customerTelNo"
                      placeholder="05xx xxx xx xx"
                      value={form.customerTelNo}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-text">{t("register.phoneHint")}</div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">{t("register.birthDate")}</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-calendar3"></i>
                    </span>
                    <BirthDatePicker
                      maxDate={maxBirthDate}
                      value={form.customerBirthDate}
                      onChange={(v) => setForm((prev) => ({ ...prev, customerBirthDate: v }))}
                    />
                  </div>
                  <div className="form-text">{t("register.birthDateHint")}</div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">{t("register.password")}</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-lock"></i>
                    </span>
                    <input
                      type="password"
                      className="form-control"
                      name="customerPassword"
                      placeholder="••••••••"
                      value={form.customerLogin.customerPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">{t("register.passwordRepeat")}</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-lock-fill"></i>
                    </span>
                    <input
                      type="password"
                      className={`form-control ${passwordError ? "is-invalid" : ""}`}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={handleConfirmChange}
                      required
                    />
                    {passwordError && (
                      <div className="invalid-feedback">{passwordError}</div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="registerAcceptTerms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="registerAcceptTerms">
                      {t("register.consentBefore")}
                      <a href={LEGAL_PATHS.terms} target="_blank" rel="noopener noreferrer" style={{ color: "#c20e1a" }}>
                        {t("legal.terms")}
                      </a>
                      {t("register.consentMiddle")}
                      <a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener noreferrer" style={{ color: "#c20e1a" }}>
                        {t("legal.privacy")}
                      </a>
                      {t("register.consentAfter")}
                    </label>
                  </div>
                </div>
              </div>

              {apiError && (
                <div className="alert alert-danger mt-3 py-2 small">
                  <i className="bi bi-exclamation-circle me-2"></i>
                  {apiError}
                </div>
              )}

              <button
                type="button"
                className="btn btn-danger w-100 rounded-pill py-2 fw-semibold mt-4"
                onClick={handleRegister}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" />
                ) : (
                  <i className="bi bi-person-check me-2"></i>
                )}
                {t("register.title")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <OtpVerificationPopup
        registerData={registerResponse}
        phoneNumber={form.customerTelNo}
        sentAt={otpSentAt}
        onPhoneVerified={openEmailModal}
        onSuccess={onLoginSuccess}
      />

      <EmailVerificationPopup
        verificationId={emailVerifId}
        onSuccess={onLoginSuccess}
      />
    </>
  );
}

export default RegisterPopup;
